/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  ArrowLeft,
  Package,
  Loader2,
  Tag,
  ArrowRight
} from 'lucide-react';
import { Product } from '../types';
import { getProductsByCategory } from '../api';
import BrandProductSection from '../components/brand/BrandProductSection';
import { usePricingRefresh } from '../hooks/usePricingRefresh';

/* ============================================================
   ANIMATION VARIANTS
   ============================================================ */
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } }
};

/* ============================================================
   HERO SECTION
   ============================================================ */
const HeroSection: React.FC<{
  categoryName: string;
  category: any;
  productCount: number;
}> = ({ categoryName, category, productCount }) => {
  const displayName = category?.name || categoryName.replace(/-/g, ' ');

  return (
    <section className="relative overflow-hidden bg-slate-900">
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ x: [0, 50, 0], y: [0, -20, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-blue-600/25 to-purple-600/15 blur-3xl"
        />
        <motion.div
          animate={{ x: [0, -30, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-tl from-emerald-500/15 to-cyan-500/15 blur-3xl"
        />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-10 md:py-14">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-sm text-white/50 mb-6"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/categories" className="hover:text-white transition-colors">Categories</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white font-medium capitalize">{displayName}</span>
          </motion.div>

          {/* Category Info */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-5">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-3 py-1 text-xs text-white/80">
                  <Tag className="w-3 h-3 text-amber-400" />
                  Category
                </span>
                {productCount > 0 && (
                  <span className="inline-flex items-center gap-1 bg-emerald-500/20 backdrop-blur-md border border-emerald-500/30 rounded-full px-3 py-1 text-xs text-emerald-300">
                    <Package className="w-3 h-3" />
                    {productCount} products
                  </span>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight capitalize">
                {displayName}
              </h1>
              {category?.description && (
                <p className="text-white/50 text-sm md:text-base max-w-xl mt-3 leading-relaxed">
                  {category.description}
                </p>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="hidden md:block"
            >
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-5 py-2.5 text-sm text-white/80 hover:bg-white/20 hover:text-white transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                All Categories
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 to-transparent" />
    </section>
  );
};

/* ============================================================
   STICKY FILTER BAR
   ============================================================ */
const FilterBar: React.FC<{
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  productCount: number;
  filteredCount: number;
}> = ({ searchQuery, setSearchQuery, productCount, filteredCount }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="sticky top-0 z-40 bg-gray-50/95 backdrop-blur-md border-b border-gray-200/80">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row gap-3 py-3 items-stretch sm:items-center">
          {/* Search */}
          <div className={`relative flex-1 max-w-md transition-all duration-300 ${isFocused ? 'transform scale-[1.01]' : ''}`}>
            <div className={`absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl blur opacity-0 transition-opacity duration-300 ${isFocused ? 'opacity-20' : ''}`} />
            <div className="relative flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
              <Search className="w-4 h-4 text-gray-400 ml-4 flex-shrink-0" />
              <input
                type="text"
                placeholder="Search in this category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className="flex-1 bg-transparent border-none outline-none px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mr-2 p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Count */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 hidden lg:inline">
              {filteredCount} of {productCount}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   SKELETON LOADING
   ============================================================ */
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
    <div className="aspect-square bg-gray-200" />
    <div className="p-4">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
      <div className="h-5 bg-gray-200 rounded w-1/3 mb-3" />
      <div className="h-10 bg-gray-200 rounded-xl" />
    </div>
  </div>
);

const LoadingState = () => (
  <div className="min-h-screen bg-gray-50">
    <div className="bg-slate-900 py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className="animate-pulse">
          <div className="h-4 bg-white/10 rounded-full w-48 mb-6" />
          <div className="h-10 bg-white/10 rounded-lg w-72 mb-2" />
          <div className="h-5 bg-white/10 rounded w-96" />
        </div>
      </div>
    </div>
    <div className="container mx-auto px-4 py-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================
   EMPTY STATE
   ============================================================ */
const EmptyState: React.FC<{ categoryName: string }> = ({ categoryName }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4 }}
    className="flex flex-col items-center justify-center py-20 text-center"
  >
    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-slate-100 to-gray-200 flex items-center justify-center mb-6">
      <Package className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-2">No products yet</h3>
    <p className="text-gray-500 mb-8 max-w-md">
      We're currently adding products to {categoryName}. Check back soon or explore other categories!
    </p>
    <Link
      to="/categories"
      className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
    >
      <ArrowLeft className="w-4 h-4" />
      Browse Categories
    </Link>
  </motion.div>
);

/* ============================================================
   ERROR STATE
   ============================================================ */
const ErrorState: React.FC<{ error: string; slug: string; onRetry: () => void }> = ({ error, slug, onRetry }) => {
  const isCategoryNotFound = error.toLowerCase().includes('category not found') || error.toLowerCase().includes('not found');

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-md"
      >
        <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center mx-auto mb-6 border border-red-100">
          <Search className="w-10 h-10 text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          {isCategoryNotFound ? 'Category Not Found' : 'Something went wrong'}
        </h2>
        <p className="text-gray-500 mb-8">
          {isCategoryNotFound
            ? `The category "${slug}" doesn't exist or has been removed.`
            : error}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {isCategoryNotFound ? (
            <>
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
              >
                <ArrowLeft className="w-4 h-4" />
                Browse Categories
              </Link>
            </>
          ) : (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
            >
              <Loader2 className="w-4 h-4" />
              Try Again
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

/* ============================================================
   BOTTOM CTA
   ============================================================ */
const BottomCTA: React.FC = () => (
  <section className="py-10 md:py-14 border-t border-gray-200/80">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
        variants={scaleIn}
        className="text-center"
      >
        <Link
          to="/categories"
          className="inline-flex items-center gap-2 text-slate-900 font-bold hover:text-blue-600 transition-colors text-base md:text-lg"
        >
          <ArrowLeft className="w-5 h-5" />
          Explore More Categories
          <ArrowRight className="w-5 h-5" />
        </Link>
      </motion.div>
    </div>
  </section>
);

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const pricingRefresh = usePricingRefresh();
  const [sections, setSections] = useState<{ _id?: string; title: string; layout: 'slider' | 'grid'; products: Product[] }[]>([]);
  const [category, setCategory] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productCount, setProductCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCategoryData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const categoryRes = await getProductsByCategory(slug!, { page: 1, limit: 100 });

      if (!categoryRes.success) {
        throw new Error(categoryRes.message || 'Failed to load category');
      }

      setCategory(categoryRes.data?.category || null);
      setSections(categoryRes.data?.sections || []);
      setProductCount(categoryRes.data?.productCount ?? 0);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load category data.';
      setError(errorMessage);
      setSections([]);
      setCategory(null);
    } finally {
      setLoading(false);
    }
  }, [slug, pricingRefresh]);

  useEffect(() => {
    if (slug) {
      fetchCategoryData();
    }
  }, [fetchCategoryData]);

  const displaySections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return sections
      .map((section) => {
        let prods = [...section.products];
        if (q) {
          prods = prods.filter((p) => p.name.toLowerCase().includes(q));
        }
        prods.sort((a, b) => (a.price?.selling || 0) - (b.price?.selling || 0));
        return { ...section, products: prods };
      })
      .filter((section) => section.products.length > 0);
  }, [sections, searchQuery]);

  const visibleProductCount = useMemo(
    () => displaySections.reduce((n, s) => n + s.products.length, 0),
    [displaySections]
  );

  const categoryName = category?.name || slug?.replace(/-/g, ' ') || 'this category';

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} slug={slug || ''} onRetry={fetchCategoryData} />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <HeroSection
        categoryName={slug || ''}
        category={category}
        productCount={productCount}
      />

      {/* Sticky Filter Bar */}
      <FilterBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        productCount={productCount}
        filteredCount={visibleProductCount}
      />

      {displaySections.length === 0 ? (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-white border-t border-gray-100">
          <EmptyState categoryName={categoryName} />
        </div>
      ) : (
        displaySections.map((section) => (
          <BrandProductSection
            key={section._id || section.title}
            title={section.title}
            layout={section.layout}
            products={section.products as any}
          />
        ))
      )}

      {/* Bottom CTA */}
      <BottomCTA />
    </div>
  );
};

export default CategoryPage;
